import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormTextArea from './FormTextArea';
import FormUtil from './FormUtil';
import FormHelpBlock from './FormHelpBlock';
import './FormTextAreaMinMax.less';

class FormTextAreaMinMax extends PureComponent {
    static displayName = 'FormTextAreaMinMax';

    static propTypes = {
        /** The textarea's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** The textarea's label, required for a11y */
        label: PropTypes.string.isRequired,
        /** The actual content of the textarea */
        value: PropTypes.string,
        /** The height, in number of lines, to make the textarea. This is also the minimum size for autoExpand textareas */
        rows: PropTypes.number,
        /** A flag to make the textarea control expand its height to fit the content */
        autoExpand: PropTypes.bool,
        /** The height as a string, in a CSS unit (px, em, vh...) to limit the textarea's height. Can be left blank, zero, or negative for no limit. */
        maxHeight: PropTypes.string,
        /** The maximum height, in number of lines, to make the textarea. This will be trumped by maxHeight */
        maxRows: PropTypes.number,
        /** For form controls with no visible label, set this to true. Make sure to still add a value to the label property. For screen readers. */
        srOnlyLabel: PropTypes.bool,
        /** The help message displayed beneath the input. */
        helpText: PropTypes.node,
        /** The error message displayed beneath the input. Styling of the input will visually indicate an error state. */
        errorMsg: PropTypes.node,
        /** Flag to make helpText and errorMsg use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** Called when the value property changes */
        onChange: PropTypes.func,
        /** Called when the textarea gains focus */
        onFocus: PropTypes.func,
        /** Enables character counter and sets maximum count (0 indicates no maximum and will not enable count display)*/
        maxChars: PropTypes.number,
        /** Enables character counter and sets minimum count (0 indicates no minimum and will not enable count display)*/
        minChars: PropTypes.number,
        /** Flag when set to true will only render character count related messages when there is a character count error state*/
        onlyShowMsgOnError: PropTypes.bool,
        /** Prop to support localization of the string being used to communicate character count. It supports interpolation by replacing {minChars} or {maxChars}. */
        lingua: PropTypes.shape({
            minCharMessage: PropTypes.string,
            minCharErrorMessage: PropTypes.string,
            maxCharErrorMessage: PropTypes.string
        })
    };

    static defaultProps = {
        autoExpand: false,
        srOnlyLabel: false,
        onlyShowMsgOnError: false,
        maxChars: 0,
        minChars: 0,
        lingua: {
            minCharMessage: '{minChars} character minimum',
            minCharErrorMessage: 'Characters remaining: {minChars}',
            maxCharErrorMessage: 'Over character limit'
        }
    };

    constructor(props) {
        super(props);
        this.state = {
            hasBeenBlurred: false
        };
    }

    handleFocus = (event) => {
        if (!this.state.hasFocus) {
            this.setState({hasFocus: true});
        }

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    }

    handleBlur = (event) => {
        if (this.state.hasFocus) {
            this.setState({
                hasFocus: false,
                hasBeenBlurred: true
            });
        }

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    }

    render() {
        const {helpText, errorMsg, maxChars, minChars, onlyShowMsgOnError, lingua, ...rest} = this.props;
        const {hasFocus, hasBeenBlurred} = this.state;
        const id = this.props.id;

        // note that below, the disabled and value props have not been destructured since they need
        // to get passed through with ...rest to the input element to be used as an attribute

        const helpBlockValues = FormUtil.computeHelpValues({
            id,
            disabled: this.props.disabled,
            value: this.props.value,
            helpText,
            errorMsg,
            onlyShowMsgOnError,
            maxChars,
            minChars,
            lingua,
            hasBeenBlurred,
            hasFocus,
            inverse: this.props.inverse
        });

        const {maxCharCountExceeded, hasError} = helpBlockValues;
        const parentClasses = classNames('FormTextAreaMinMax', {'has-error': hasError, 'has-focus': hasFocus});

        const textAreaProps = {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            // Computing which errors are being displayed and assigning respective aria-describedby values
            ...(FormUtil.needAriaMsg({
                errorMsg,
                helpText,
                minChars,
                maxCharCountExceeded
            }) && {'aria-describedby': FormUtil.computeAriaMsgList({
                id,
                errorMsg,
                helpText,
                minChars,
                maxCharCountExceeded
            })}),
            ...rest
        };

        return (
            <div className={parentClasses}>
                <FormTextArea {...textAreaProps}/>
                <FormHelpBlock {...helpBlockValues} />
            </div>
        );
    }
}

export default FormTextAreaMinMax;
